import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../api/adminApi';
import { IdControl } from '@shared-ui/components';
import { LatexText } from '@shared-ui/latex';

interface CheckResult {
  name: string;
  status: 'pass' | 'fail';
  count: number;
  checkedCount?: number;
  newIssueCount?: number;
  items?: unknown[];
  executedOn?: string;
}

interface CheckState {
  id: string;
  name: string;
  result: CheckResult | null;
  expanded: boolean;
}

interface ScanProgress {
  scanned: number;
  cursorTs: number;
  reachedEnd: boolean;
  cap: number;
  reset: boolean;
}

interface GroupState {
  id: string;
  name: string;
  checks: CheckState[];
  loading: boolean;
  error: string | null;
  /** Rolling-watermark group (scanned one slice per run). */
  incremental: boolean;
  cap?: number;
  progress: ScanProgress | null;
  collapsed: boolean;
}

export function Diagnosis() {
  const [groups, setGroups] = useState<GroupState[]>([]);
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => {
    adminApi.listDiagnosisGroups().then(data => {
      setGroups(data.groups.map(g => ({
        id: g.id,
        name: g.name,
        incremental: g.incremental ?? false,
        cap: g.cap,
        progress: null,
        collapsed: false,
        checks: g.checks.map(c => ({
          id: c.id,
          name: c.name,
          result: c.lastResult ?? null,
          expanded: false,
        })),
        loading: false,
        error: null,
      })));
    }).catch(() => {}).finally(() => setLoadingList(false));
  }, []);

  function toggleGroupCollapse(groupIdx: number) {
    setGroups(prev => prev.map((g, gi) => gi === groupIdx ? { ...g, collapsed: !g.collapsed } : g));
  }

  function toggleExpand(groupIdx: number, checkIdx: number) {
    setGroups(prev => prev.map((g, gi) => gi === groupIdx
      ? { ...g, checks: g.checks.map((c, ci) => ci === checkIdx ? { ...c, expanded: !c.expanded } : c) }
      : g
    ));
  }

  async function runGroup(groupIdx: number, reset = false) {
    const group = groups[groupIdx];

    setGroups(prev => prev.map((g, gi) => gi === groupIdx ? { ...g, loading: true, error: null } : g));

    try {
      const data = await adminApi.runDiagnosisGroup(
        group.id,
        group.checks.map(c => c.id),
        reset ? { reset: 'true' } : {},
      );

      // Map results back to checks by index in the response
      const resultMap = new Map(data.checks.map((r, i) => [group.checks[i].id, r]));

      setGroups(prev => prev.map((g, gi) => gi === groupIdx
        ? {
            ...g,
            loading: false,
            progress: data.progress ?? null,
            checks: g.checks.map(c => {
              const result = resultMap.get(c.id);
              if (!result) return c;
              return { ...c, result, expanded: result.count > 0 };
            }),
          }
        : g
      ));
    } catch (err) {
      setGroups(prev => prev.map((g, gi) => gi === groupIdx
        ? { ...g, loading: false, error: err instanceof Error ? err.message : 'Failed' }
        : g
      ));
    }
  }

  // Collect all known issues from last-run results
  const knownIssues = groups.flatMap(g =>
    g.checks
      .filter(c => c.result && c.result.status === 'fail' && c.result.count > 0)
      .map(c => ({
        groupName: g.name,
        checkName: c.result!.name || c.name,
        count: c.result!.count,
        executedOn: c.result!.executedOn,
      }))
  );

  if (loadingList) return <div className="loading">Loading checks...</div>;

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Data Diagnosis</h2>
        <p className="section-description">
          Run integrity checks to detect data anomalies. Select checks within each group and run them together.
        </p>
      </div>

      {/* Known Issues summary */}
      {!loadingList && (
        <div style={{
          marginBottom: '16px',
          border: `1px solid ${knownIssues.length > 0 ? 'var(--status-error-border, #fca5a5)' : 'var(--status-success-border, #86efac)'}`,
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '10px 14px',
            background: knownIssues.length > 0 ? 'var(--status-error-bg, #fee2e2)' : 'var(--status-success-bg, #dcfce7)',
            fontWeight: 600,
            fontSize: '0.95rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span>{knownIssues.length > 0 ? '\u26A0' : '\u2713'}</span>
            <span>
              {knownIssues.length > 0
                ? `${knownIssues.length} Known Issue${knownIssues.length !== 1 ? 's' : ''} (${knownIssues.reduce((s, i) => s + i.count, 0)} total items)`
                : groups.some(g => g.checks.some(c => c.result)) ? 'No Known Issues' : 'No checks have been run yet'}
            </span>
          </div>
          {knownIssues.length > 0 && (
            <table className="dt dt--bare dt--compact" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Check</th>
                  <th>Group</th>
                  <th style={{ textAlign: 'right' }}>Issues</th>
                  <th style={{ textAlign: 'right' }}>Last Checked</th>
                </tr>
              </thead>
              <tbody>
                {knownIssues.map((issue, idx) => (
                  <tr key={idx}>
                    <td style={{ fontWeight: 500 }}>{issue.checkName}</td>
                    <td style={{ color: 'var(--text-secondary, #666)' }}>{issue.groupName}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--status-error-text, #dc2626)' }}>{issue.count}</td>
                    <td style={{ textAlign: 'right', fontSize: '0.8rem', color: 'var(--text-tertiary, #8b949e)' }}>
                      {issue.executedOn ? new Date(issue.executedOn).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {groups.map((group, gIdx) => (
          <div key={group.id} style={{
            border: '1px solid var(--border-color, #d0d7de)',
            borderRadius: '8px',
            overflow: 'hidden',
          }}>
            {/* Group header (click to collapse/expand) */}
            <div
              onClick={() => toggleGroupCollapse(gIdx)}
              style={{
                padding: '10px 14px',
                background: 'var(--bg-secondary, #f6f8fa)',
                borderBottom: group.collapsed ? undefined : '1px solid var(--border-color, #d0d7de)',
                fontWeight: 600,
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span style={{ fontSize: '10px', color: 'var(--text-secondary, #666)' }}>{group.collapsed ? '▶' : '▼'}</span>
              <span>{group.name}</span>
              {(() => {
                const issues = group.checks.reduce((s, c) => s + (c.result?.status === 'fail' ? c.result.count : 0), 0);
                const ran = group.checks.some(c => c.result);
                return (
                  <span style={{ marginLeft: 'auto', fontSize: '0.8rem', fontWeight: 500, color: issues > 0 ? 'var(--status-error-text, #dc2626)' : 'var(--text-secondary, #666)' }}>
                    {issues > 0 ? `✗ ${issues} issue${issues !== 1 ? 's' : ''}` : ran ? '✓ No issues' : ''}
                  </span>
                );
              })()}
            </div>

            {!group.collapsed && (<>
            {/* Checks list */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {group.checks.map((check, cIdx) => (
                <div key={check.id}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 14px',
                    background: check.result
                      ? (check.result.status === 'pass' ? 'var(--status-success-bg, #dcfce7)' : 'var(--status-error-bg, #fee2e2)')
                      : 'transparent',
                    borderBottom: cIdx < group.checks.length - 1 ? '1px solid var(--border-color, #eee)' : undefined,
                  }}>
                    <span style={{ flex: 1, fontWeight: 500 }}>{check.name}</span>

                    {check.result && (
                      <>
                        <span
                          style={{
                            fontWeight: 600, fontSize: '0.85rem',
                            cursor: check.result.count > 0 ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', gap: '4px',
                          }}
                          onClick={() => check.result && check.result.count > 0 && toggleExpand(gIdx, cIdx)}
                        >
                          {check.result.status === 'pass' ? '\u2713 Pass' : `\u2717 ${check.result.count} issue${check.result.count !== 1 ? 's' : ''}`}
                          {check.result.count > 0 && (
                            <span style={{ fontSize: '10px' }}>{check.expanded ? '\u25BC' : '\u25B6'}</span>
                          )}
                        </span>
                        {check.result.checkedCount != null && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #666)' }}>
                            checked {check.result.checkedCount}{check.result.newIssueCount != null ? `, ${check.result.newIssueCount} new` : ''}
                          </span>
                        )}
                        {check.result.executedOn && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary, #8b949e)' }}>
                            {new Date(check.result.executedOn).toLocaleString()}
                          </span>
                        )}
                        <ExportCheckButton checkId={check.id} items={check.result.items as Record<string, unknown>[] | undefined} />
                      </>
                    )}

                    {!check.result && (
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-tertiary, #8b949e)' }}>--</span>
                    )}
                  </div>

                  {/* Expanded results */}
                  {check.expanded && check.result?.items && check.result.items.length > 0 && (
                    <div style={{ padding: '0 4px 4px' }}>
                      <DiagnosisItemsTable checkId={check.id} items={check.result.items as Record<string, unknown>[]} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Group footer: params + run button */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px',
              padding: '8px 14px',
              borderTop: '1px solid var(--border-color, #d0d7de)',
              background: 'var(--bg-secondary, #f6f8fa)',
            }}>
              {group.error && (
                <span style={{ color: 'var(--status-error-text, #dc2626)', fontSize: '0.85rem', marginRight: 'auto' }}>
                  Error: {group.error}
                </span>
              )}

              {group.incremental && !group.error && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #666)', marginRight: 'auto' }}>
                  {group.progress
                    ? (() => {
                        const coveredUpTo = group.progress.cursorTs > 0
                          ? new Date(group.progress.cursorTs * 1000).toLocaleString()
                          : null;
                        const covered = coveredUpTo ? ` Covered up to ${coveredUpTo} (modified-on).` : '';
                        return group.progress.reachedEnd
                          ? `✓ Caught up — scanned ${group.progress.scanned} last run.${covered} New/edited questions are picked up automatically.`
                          : `Scanned ${group.progress.scanned} this run — more pending.${covered} Run again to continue the sweep.`;
                      })()
                    : `Scans the next ${group.cap ?? 1000} questions (by last-modified) per run, accumulating results.`}
                </span>
              )}

              {group.incremental && (
                <button
                  className="explorer-fetch-btn"
                  onClick={() => runGroup(gIdx, true)}
                  disabled={group.loading}
                  title="Reset the watermark and re-scan the whole corpus from the start"
                  style={{ padding: '4px 12px', fontSize: '0.8rem', background: 'transparent', color: 'var(--text-primary, #333)' }}
                >
                  Full re-sweep
                </button>
              )}

              <button
                className="explorer-fetch-btn"
                onClick={() => runGroup(gIdx)}
                disabled={group.loading}
                style={{ padding: '4px 12px', fontSize: '0.8rem' }}
              >
                {group.loading ? 'Running...' : group.incremental ? `Scan next ${group.cap ?? 1000}` : 'Run All Checks'}
              </button>
            </div>
            </>)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Column definitions per check type
const CHECK_COLUMNS: Record<string, { key: string; label: string; mono?: boolean; maxWidth?: number }[]> = {
  StudentUsedDraftQuestions: [
    { key: 'questionText', label: 'Question', maxWidth: 400 },
    { key: 'subject', label: 'Subject' },
    { key: 'attemptedByCount', label: 'Students' },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  LiveQuestionsMissingType: [
    { key: 'questionText', label: 'Question', maxWidth: 400 },
    { key: 'subject', label: 'Subject' },
    { key: 'importStatus', label: 'Status' },
    { key: 'currentType', label: 'Current Type' },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  BrokenQuestionUrls: [
    { key: 'questionText', label: 'Question', maxWidth: 300 },
    { key: 'subject', label: 'Subject' },
    { key: 'importStatus', label: 'Status' },
    { key: 'field', label: 'Field' },
    { key: 'httpStatus', label: 'HTTP Status' },
    { key: 'url', label: 'URL', mono: true, maxWidth: 300 },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  InvalidLatex: [
    { key: 'questionText', label: 'Question', maxWidth: 300 },
    { key: 'subject', label: 'Subject' },
    { key: 'importStatus', label: 'Status' },
    { key: 'field', label: 'Field' },
    { key: 'latexSnippet', label: 'LaTeX Snippet', mono: true, maxWidth: 200 },
    { key: 'error', label: 'Error', maxWidth: 300 },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  SolutionsMissingExpectedTime: [
    { key: 'questionText', label: 'Question', maxWidth: 300 },
    { key: 'subject', label: 'Subject' },
    { key: 'importStatus', label: 'Status' },
    { key: 'solutionCount', label: 'Solutions' },
    { key: 'missingCount', label: 'Missing' },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  OversizedSolutions: [
    { key: 'questionText', label: 'Question', maxWidth: 300 },
    { key: 'subject', label: 'Subject' },
    { key: 'importStatus', label: 'Status' },
    { key: 'solutionStyle', label: 'Style' },
    { key: 'lineCount', label: 'Lines' },
    { key: 'maxAllowed', label: 'Max' },
    { key: 'questionId', label: 'Question ID', mono: true },
  ],
  MissingPrimaryTargetExam: [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'userType', label: 'Type' },
    { key: 'targetExams', label: 'Target Exams' },
    { key: 'userId', label: 'User ID', mono: true },
  ],
  MissingTargetExams: [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'userType', label: 'Type' },
    { key: 'primaryTargetExam', label: 'Primary Exam' },
    { key: 'userId', label: 'User ID', mono: true },
  ],
};

function DiagnosisItemsTable({ checkId, items }: { checkId: string; items: Record<string, unknown>[] }) {
  const columns = CHECK_COLUMNS[checkId];
  const [statusFilter, setStatusFilter] = useState<'all' | 'live' | 'draft'>('all');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');

  // Detect which fields the items carry, to decide which filters to show.
  const hasStatus = items.length > 0 && 'importStatus' in items[0];
  const hasSubject = items.length > 0 && 'subject' in items[0];

  // Distinct subjects present in the items (for the subject dropdown).
  const subjects = useMemo(
    () => Array.from(new Set(items.map(i => i.subject).filter(Boolean) as string[])).sort(),
    [items],
  );

  const filteredItems = useMemo(
    () => items.filter(item =>
      (!hasStatus || statusFilter === 'all' || item.importStatus === statusFilter) &&
      (!hasSubject || subjectFilter === 'all' || item.subject === subjectFilter)
    ),
    [items, statusFilter, subjectFilter, hasStatus, hasSubject],
  );

  // Fallback: auto-detect columns from first item
  const cols = columns || Object.keys(items[0] || {}).map(key => ({ key, label: key }));

  return (
    <div>
      {(hasStatus || (hasSubject && subjects.length > 1)) && (
        <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', gap: '14px', fontSize: '0.8rem', flexWrap: 'wrap' }}>
          {hasStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--text-secondary, #666)' }}>Status:</span>
              {(['all', 'live', 'draft'] as const).map(val => (
                <button
                  key={val}
                  onClick={() => setStatusFilter(val)}
                  style={{
                    padding: '2px 8px',
                    fontSize: '0.75rem',
                    borderRadius: '4px',
                    border: `1px solid ${statusFilter === val ? 'var(--color-primary)' : 'var(--border-color, #d0d7de)'}`,
                    background: statusFilter === val ? 'var(--color-primary)' : 'transparent',
                    color: statusFilter === val ? '#fff' : 'var(--text-primary, #333)',
                    cursor: 'pointer',
                  }}
                >
                  {val === 'all' ? `All (${items.length})` : `${val.charAt(0).toUpperCase() + val.slice(1)} (${items.filter(i => i.importStatus === val).length})`}
                </button>
              ))}
            </div>
          )}
          {hasSubject && subjects.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--text-secondary, #666)' }}>Subject:</span>
              <select
                value={subjectFilter}
                onChange={e => setSubjectFilter(e.target.value)}
                style={{
                  padding: '2px 6px',
                  fontSize: '0.75rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color, #d0d7de)',
                  background: 'transparent',
                  color: 'var(--text-primary, #333)',
                  cursor: 'pointer',
                }}
              >
                <option value="all">All ({items.length})</option>
                {subjects.map(s => (
                  <option key={s} value={s}>
                    {s.charAt(0).toUpperCase() + s.slice(1)} ({items.filter(i => i.subject === s).length})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      <table className="dt dt--bare dt--compact">
        <thead>
          <tr>
            {cols.map(col => <th key={col.key}>{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item, idx) => (
            <tr key={(item.questionId as string) || idx}>
              {cols.map(col => (
                <td
                  key={col.key}
                  style={{
                    ...(col.maxWidth ? { maxWidth: col.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const } : {}),
                    ...(col.mono && col.key !== 'questionId' && col.key !== 'userId' ? { fontFamily: 'monospace', fontSize: '11px' } : {}),
                  }}
                >
                  {col.key === 'questionId' && item[col.key]
                    ? <IdControl value={String(item[col.key])} container="questions" />
                    : col.key === 'userId' && item[col.key]
                    ? <IdControl value={String(item[col.key])} container="users" />
                    : col.key === 'importStatus'
                    ? <span style={{
                        padding: '1px 6px',
                        borderRadius: '3px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        background: item[col.key] === 'draft' ? 'var(--status-warning-bg, #fef3c7)' : 'var(--status-success-bg, #dcfce7)',
                        color: item[col.key] === 'draft' ? 'var(--status-warning-text, #92400e)' : 'var(--status-success-text, #166534)',
                      }}>{String(item[col.key] ?? 'live')}</span>
                    : col.key === 'questionText'
                    ? <LatexText text={String(item[col.key] ?? '')} />
                    : String(item[col.key] ?? '-')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Per-check export button. Reuses the question import/export JSON format
 *  by hitting `/console/questions/export-by-ids` with the questionIds drawn
 *  from the check's items, so the result re-imports cleanly through the
 *  import wizard. Renders nothing for non-question checks (no `questionId`
 *  field in the items). */
function ExportCheckButton({ checkId, items }: { checkId: string; items: Record<string, unknown>[] | undefined }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ids = useMemo(() => {
    if (!items || items.length === 0) return [];
    const seen = new Set<string>();
    for (const it of items) {
      const id = it.questionId;
      if (typeof id === 'string' && id) seen.add(id);
    }
    return Array.from(seen);
  }, [items]);

  if (ids.length === 0) return null;

  async function onClick() {
    setBusy(true);
    setErr(null);
    try {
      const result = await adminApi.exportQuestionsByIds(ids, true);
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `diagnosis-${checkId}-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={busy}
      title={err ?? `Export ${ids.length} question${ids.length === 1 ? '' : 's'} as JSON`}
      style={{
        padding: '2px 8px',
        fontSize: '0.75rem',
        borderRadius: '4px',
        border: '1px solid var(--border-color, #d0d7de)',
        background: err ? 'var(--status-error-bg, #fee2e2)' : 'transparent',
        color: err ? 'var(--status-error-text, #dc2626)' : 'var(--text-primary, #333)',
        cursor: busy ? 'wait' : 'pointer',
      }}
    >
      {busy ? 'Exporting…' : err ? 'Retry export' : `Export (${ids.length})`}
    </button>
  );
}
